import { MessageCircleQuestion, Minus, RotateCcw, SkipForward, X } from '@tamagui/lucide-icons-2'
import { useEffect, useRef } from 'react'
import Button from 'components/button'
import { AnimatePresence, SizableText, View, XStack, YStack } from 'tamagui'
import AiHelpInput from './AiHelpInput'
import AiHelpMessageBubble, { TypingIndicator } from './AiHelpMessageBubble'
import { useAiHelp } from './AiHelpProvider'
import { useAiHelpRestore } from './useAiHelpRestore'
import { useAiHelpSend } from './useAiHelpSend'

const quickStarts = [
    'How do I add a new client?',
    'What are the client pipeline stages?',
    'How do I set up e-signing?',
    'How do permissions and roles work?',
]

function WelcomeMessage({ onQuickStart }: { onQuickStart: (q: string) => void }) {
    return (
        <YStack flex={1} alignItems="center" justifyContent="center" padding="$4" gap="$4">
            <View
                width={56}
                height={56}
                borderRadius={28}
                backgroundColor="$primaryLight"
                alignItems="center"
                justifyContent="center"
            >
                <MessageCircleQuestion size={28} color="$primary" />
            </View>
            <YStack alignItems="center" gap="$2">
                <SizableText size="$5" fontWeight="600">
                    Buyerflo Help
                </SizableText>
                <SizableText size="$3" color="$gray10" textAlign="center">
                    Ask me anything about using Buyerflo. I can guide you through features step by step.
                </SizableText>
            </YStack>
            <YStack gap="$2" width="100%">
                {quickStarts.map(q => (
                    <Button
                        key={q}
                        size="$3"
                        variant="outlined"
                        borderRadius="$3"
                        onPress={() => onQuickStart(q)}
                    >
                        <SizableText size="$2">{q}</SizableText>
                    </Button>
                ))}
            </YStack>
        </YStack>
    )
}

export default function AiHelpWidget({ onboardingSidebarOpen }: { onboardingSidebarOpen?: boolean }) {
    const rightOffset = onboardingSidebarOpen ? 344 : 24
    const {
        isExpanded,
        setIsExpanded,
        messages,
        isLoading,
        clearChat,
        clearHighlights,
        isInStepFlow,
        steps,
        currentStepIndex,
        advanceStep,
        exitStepFlow,
    } = useAiHelp()
    const send = useAiHelpSend()
    useAiHelpRestore()
    const scrollRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight
        }
    }, [messages, isLoading])

    const currentStep = isInStepFlow ? steps[currentStepIndex] : null

    return (
        <>
            {/* Collapsed bar */}
            {!isExpanded && (
                <>
                    {isInStepFlow && currentStep ? (
                        /* Step indicator bar */
                        <XStack
                            position="fixed"
                            bottom={24}
                            right={rightOffset}
                            zIndex={1200}
                            backgroundColor="$background"
                            borderWidth={1}
                            borderColor="$primary"
                            borderRadius="$4"
                            elevation="$4"
                            alignItems="center"
                            gap="$2"
                            paddingHorizontal="$3"
                            paddingVertical="$2.5"
                            width={340}
                            maxWidth="calc(100vw - 48px)"
                            animation="quick"
                            cursor="pointer"
                            onPress={() => setIsExpanded(true)}
                            hoverStyle={{ elevation: '$6' }}
                        >
                            {/* Step counter badge */}
                            <View
                                width={26}
                                height={26}
                                borderRadius={13}
                                backgroundColor="$primary"
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                            >
                                <SizableText size="$1" color="white" fontWeight="700">
                                    {currentStepIndex + 1}/{steps.length}
                                </SizableText>
                            </View>
                            <SizableText size="$3" flex={1} numberOfLines={1}>
                                {currentStep.description}
                            </SizableText>
                            <Button
                                size="$2"
                                circular
                                chromeless
                                onPress={(e: any) => {
                                    e.stopPropagation()
                                    exitStepFlow()
                                }}
                            >
                                <X size={16} />
                            </Button>
                        </XStack>
                    ) : (
                        /* Regular help toggle button */
                        <Button
                            position="fixed"
                            bottom={24}
                            right={rightOffset}
                            zIndex={1200}
                            width={48}
                            height={48}
                            circular
                            backgroundColor="$primary"
                            elevation="$4"
                            animation="quick"
                            onPress={() => setIsExpanded(true)}
                            hoverStyle={{ elevation: '$6', scale: 1.05, backgroundColor: '$blue10' }}
                            pressStyle={{ scale: 0.95, backgroundColor: '$blue10' }}
                        >
                            <MessageCircleQuestion size={22} color="white" />
                        </Button>
                    )}
                </>
            )}

            {/* Expanded panel */}
            <AnimatePresence>
                {isExpanded && (
                    <YStack
                        position="fixed"
                        bottom={24}
                        right={rightOffset}
                        width={380}
                        maxWidth="calc(100vw - 48px)"
                        backgroundColor="$background"
                        borderWidth={1}
                        borderColor="$borderColor"
                        borderRadius="$5"
                        zIndex={1300}
                        elevation="$6"
                        overflow="hidden"
                        animation="quick"
                        enterStyle={{ y: 20, opacity: 0, scale: 0.97 }}
                        exitStyle={{ y: 20, opacity: 0, scale: 0.97 }}
                        {...(isInStepFlow
                            ? { height: 'auto', maxHeight: 'calc(100vh - 60px)' }
                            : { height: 480, maxHeight: 'calc(100vh - 60px)' })}
                    >
                        {isInStepFlow && currentStep ? (
                            /* Step flow expanded view */
                            <>
                                {/* Header */}
                                <XStack
                                    paddingVertical="$3"
                                    paddingHorizontal="$4"
                                    alignItems="center"
                                    borderBottomWidth={1}
                                    borderBottomColor="$borderColor"
                                    gap="$2"
                                >
                                    <View
                                        width={26}
                                        height={26}
                                        borderRadius={13}
                                        backgroundColor="$primary"
                                        alignItems="center"
                                        justifyContent="center"
                                        flexShrink={0}
                                    >
                                        <SizableText size="$1" color="white" fontWeight="700">
                                            {currentStepIndex + 1}
                                        </SizableText>
                                    </View>
                                    <SizableText size="$4" fontWeight="600" flex={1}>
                                        Step {currentStepIndex + 1} of {steps.length}
                                    </SizableText>
                                    <Button
                                        size="$2"
                                        circular
                                        chromeless
                                        onPress={() => setIsExpanded(false)}
                                    >
                                        <Minus size={18} />
                                    </Button>
                                </XStack>

                                {/* Step content */}
                                <YStack padding="$4" gap="$3">
                                    <SizableText size="$4">
                                        {currentStep.description}
                                    </SizableText>
                                    {currentStep.helpId && (
                                        <SizableText size="$2" color="$gray9">
                                            Click the highlighted element to continue
                                        </SizableText>
                                    )}
                                </YStack>

                                {/* Action buttons */}
                                <XStack
                                    padding="$3"
                                    borderTopWidth={1}
                                    borderTopColor="$borderColor"
                                    gap="$2"
                                    justifyContent="flex-end"
                                >
                                    <Button
                                        size="$3"
                                        variant="outlined"
                                        onPress={exitStepFlow}
                                    >
                                        <X size={14} />
                                        <SizableText size="$2">Exit</SizableText>
                                    </Button>
                                    <Button
                                        size="$3"
                                        backgroundColor="$primary"
                                        onPress={advanceStep}
                                    >
                                        <SkipForward size={14} color="white" />
                                        <SizableText size="$2" color="white">Skip</SizableText>
                                    </Button>
                                </XStack>
                            </>
                        ) : (
                            /* Normal chat view */
                            <>
                                {/* Header */}
                                <XStack
                                    paddingVertical="$3"
                                    paddingHorizontal="$4"
                                    alignItems="center"
                                    borderBottomWidth={1}
                                    borderBottomColor="$borderColor"
                                    gap="$2"
                                >
                                    <MessageCircleQuestion size={20} color="$primary" />
                                    <SizableText size="$4" fontWeight="600" flex={1}>
                                        Help Assistant
                                    </SizableText>
                                    {messages.length > 0 && (
                                        <Button
                                            size="$2"
                                            circular
                                            chromeless
                                            onPress={() => {
                                                clearChat()
                                                clearHighlights()
                                            }}
                                        >
                                            <RotateCcw size={16} />
                                        </Button>
                                    )}
                                    <Button
                                        size="$2"
                                        circular
                                        chromeless
                                        onPress={() => {
                                            setIsExpanded(false)
                                            clearHighlights()
                                        }}
                                    >
                                        <Minus size={18} />
                                    </Button>
                                </XStack>

                                {/* Messages */}
                                {messages.length === 0 && !isLoading ? (
                                    <WelcomeMessage onQuickStart={(q) => { send(q) }} />
                                ) : (
                                    <YStack
                                        ref={scrollRef as any}
                                        flex={1}
                                        overflow="auto"
                                        paddingVertical="$3"
                                        gap="$3"
                                    >
                                        {messages.map((msg, i) => (
                                            <AiHelpMessageBubble key={i} message={msg} />
                                        ))}
                                        {isLoading && <TypingIndicator />}
                                    </YStack>
                                )}

                                {/* Input */}
                                <AiHelpInput autoFocus />
                            </>
                        )}
                    </YStack>
                )}
            </AnimatePresence>
        </>
    )
}
